/*
 * $Id:: StepTwo.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import Input from '@material-ui/core/Input';
import InputLabel from '@material-ui/core/InputLabel';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import MenuItem from '@material-ui/core/MenuItem';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import Select from '@material-ui/core/Select';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import inputsMaxChars from '../../../../app/inputsMaxChars';
import { ReactComponent as ListArrowIcon } from '../../../../assets/images/arrow_right_24px.svg';
import { ReactComponent as SelectedChipIcon } from '../../../../assets/images/check_24px.svg';
import { ReactComponent as DropDownArrow } from '../../../../assets/images/unfold_more_24px.svg';
import { t } from '../../../../providers';

const useStyles = makeStyles((theme) => ({
    stepTwoWrapper: {
        display: 'flex',
        flexFlow: 'column nowrap',
    },
    accountTypeRadio: {
        marginBottom: '25px',
    },
    radioTitle: {
        marginTop: '10px',
        '& .MuiFormControlLabel-label': {
            color: theme.palette.text.mainGreen,
        },
        '& .MuiRadio-root': {
            color: theme.palette.text.mainGreen,
        },
    },
    title: {
        fontWeight: theme.typography.titleFontWeight,
        fontSize: theme.typography.titleFontSize,
    },
    listIcon: {
        '& path': {
            fill: theme.palette.text.listgrey,
        },
    },
    typeTabs: {
        marginTop: '10px',
        display: 'flex',
        flexFlow: 'row wrap',
    },
    tabChip: {
        marginBottom: '10px',
        color: theme.palette.text.mainGreen,
        '&:not(:last-child)': {
            marginRight: '10px',
        },
    },
    selectedTab: {
        boxShadow: '0px 1px 8px rgba(0, 0, 0, 0.1), 0px 3px 4px rgba(0, 0, 0, 0.02), 0px 3px 3px rgba(0, 0, 0, 0.04)',
        backgroundColor: '#FAFAFA',
        border: '1px solid #ECEFF1',
    },
    formWrapper: {
        marginTop: '30px',
    },
    gridItem: {
        flex: '1 1 auto',
        marginBottom: '1.5rem',
    },
    fieldLabel: {
        fontWeight: theme.typography.titleFontWeight,
        fontSize: theme.typography.titleFontSize,
        color: theme.palette.text.mainGreen,
    },
    textInput: {
        height: '2.5rem',
        width: '14rem',
        marginTop: '10px',
        '@media (max-width:600px)': {
            width: '12.5rem',
        },
    },
    buttonGroup: {
        marginTop: '40px',
        '& .MuiButton-text': {
            color: theme.palette.text.mainGreen,
        },
    },
    submitButton: {
        minWidth: '82px',
    },
    disabledMenuItem: {
        color: '#5D5D5D',
        opacity: '1 !important',
    },
}));

const StepTwo = ({ backToStepOne, proceedToVerification, error, accountTypes, legalStatusOptions, candidateUser = {} }) => {
    const classes = useStyles();

    const [userType, setUserType] = useState('');
    const [subTypes, setSubTypes] = useState([]);
    const [selectedSubType, setSelectedSubType] = useState('');
    const [legalStatus, setLegalStatus] = useState({ value: '', isDirty: false });
    const [firstName, setFirstName] = useState({ value: '', isDirty: false });
    const [surName, setSurName] = useState({ value: '', isDirty: false });
    const [legalName, setLegalName] = useState({ value: '', isDirty: false });
    const [organizationName, setOrganizationName] = useState({ value: '', isDirty: false });

    useEffect(() => {
        if (candidateUser.userType) {
            const userTypeObject = accountTypes.find(({ value }) => value === candidateUser.userType);
            if (userTypeObject && userTypeObject.subTypes) {
                setUserType(candidateUser.userType);
                setSubTypes(userTypeObject.subTypes);
            }
        }

        if (candidateUser.accountType) {
            setSelectedSubType(candidateUser.accountType);
        }

        if (candidateUser.legalStatus) setLegalStatus(candidateUser.legalStatus);

        [
            { key: 'legalStatus', setter: setLegalStatus },
            { key: 'firstName', setter: setFirstName },
            { key: 'surName', setter: setSurName },
            { key: 'organizationName', setter: setOrganizationName },
            { key: 'legalName', setter: setLegalName },
        ].forEach(({ key, setter }) => {
            if (!!candidateUser[key]) setter({ value: candidateUser[key], isDirty: true });
        });
        if (candidateUser.associationName) setOrganizationName({ value: candidateUser.associationName, isDirty: true });
    }, [candidateUser, accountTypes]);

    const resetInputs = () => {
        setFirstName({ value: '', isDirty: false });
        setSurName({ value: '', isDirty: false });
        setOrganizationName({ value: '', isDirty: false });
    };

    const resetForm = (statusLegal = '') => {
        setLegalStatus({ value: statusLegal, isDirty: !!statusLegal });
        resetInputs();
    };

    const handleChange = (event) => {
        if (userType === event.target.value) return;
        const userTypeObject = accountTypes.find(({ value }) => value === event.target.value);
        if (userTypeObject && userTypeObject.subTypes) {
            setUserType(event.target.value);
            setSubTypes(userTypeObject.subTypes);
            setSelectedSubType('');
            resetForm();
        }
    };

    const goBack = (event) => {
        event.preventDefault();
        backToStepOne();
    };

    const chooseSubType = (event, value) => {
        event.preventDefault();
        if (value === selectedSubType) return;
        const mustBeBusiness = value === 'association' || value === 'euipo.professional.practitioner';
        const newLegalStatus = mustBeBusiness ? 'business.company' : '';
        resetForm(newLegalStatus);
        setSelectedSubType(value);
    };

    const handleLegalStatusChange = (event) => {
        const value = event.target.value;
        if (value !== legalStatus.value) {
            setLegalStatus({ ...legalStatus, value: event.target.value, isDirty: true });
            resetInputs();
        }
    };

    const handleFirstNameChange = (event) => {
        if (!inputsMaxChars.firstName || event.target.value.length <= inputsMaxChars.firstName)
            setFirstName({ ...firstName, value: event.target.value, isDirty: true });
    };

    const handleLastNameChange = (event) => {
        if (!inputsMaxChars.lastName || event.target.value.length <= inputsMaxChars.lastName)
            setSurName({ ...surName, value: event.target.value, isDirty: true });
    };

    const handleLegalNameChange = (event) => {
        if (!inputsMaxChars.legalName || event.target.value.length <= inputsMaxChars.legalName)
            setLegalName({ ...legalName, value: event.target.value, isDirty: true });
    };

    const handleOrganizationNameChange = (event) => {
        if (!inputsMaxChars.organisationName || event.target.value.length <= inputsMaxChars.organisationName)
            setOrganizationName({ ...organizationName, value: event.target.value, isDirty: true });
    };

    const showLegalStatus = !!selectedSubType && userType === 'representative';

    const showFirstName =
        (userType === 'applicant' && selectedSubType === 'individuals') || (userType === 'representative' && legalStatus.value === 'individuals');

    const showSurName =
        (userType === 'applicant' && selectedSubType === 'individuals') || (userType === 'representative' && legalStatus.value === 'individuals');

    const showLegalName =
        (userType === 'applicant' && selectedSubType === 'business.company') ||
        (userType === 'representative' && selectedSubType === 'employee.representative' && legalStatus.value === 'business.company');

    const showOrganisationName =
        userType === 'representative' &&
        !!selectedSubType &&
        selectedSubType !== 'employee.representative' &&
        legalStatus.value === 'business.company';

    const submitStep = (event) => {
        event.preventDefault();
        let canProceed = true;
        let candidate = { userType };
        [
            { isShown: showFirstName, field: firstName, key: 'firstName', setterFn: setFirstName },
            { isShown: showSurName, field: surName, key: 'surName', setterFn: setSurName },
            {
                isShown: showOrganisationName,
                field: organizationName,
                key: 'organizationName',
                setterFn: setOrganizationName,
            },
            { isShown: showLegalStatus, field: legalStatus, key: 'legalStatus', setterFn: setLegalStatus },
            { isShown: showLegalName, field: legalName, key: 'legalName', setterFn: setLegalName },
        ].forEach(({ isShown, field, key, setterFn }) => {
            if (isShown && (!field.isDirty || !field.value)) {
                setterFn({ ...field, isDirty: true });
                canProceed = false;
            } else if (isShown) candidate[key] = field.value;
        });
        if (!canProceed) return;
        candidate.accountType = selectedSubType;
        proceedToVerification(candidate);
    };

    return (
        <div className={classes.stepTwoWrapper}>
            <form onSubmit={submitStep} noValidate>
                <Typography className={classes.title}>{t('select.accurate.option.to.role')}</Typography>
                <FormControl component={'fieldset'} className={classes.accountTypeRadio}>
                    <RadioGroup aria-label={'userType'} name={'userType'} value={userType} onChange={handleChange} style={{ color: '#3F5E20' }}>
                        {(accountTypes || []).map(({ key, value, descriptionItems }, index) => {
                            return (
                                <div key={`${key}_${index}`}>
                                    <FormControlLabel className={classes.radioTitle} value={value} control={<Radio disableRipple />} label={t(key)} />
                                    <List disablePadding>
                                        {descriptionItems.map((literal, indx) => (
                                            <ListItem key={`${literal}_${indx}`}>
                                                <ListItemIcon>
                                                    <ListArrowIcon className={classes.listIcon} aria-hidden="true" focusable="false" />
                                                </ListItemIcon>
                                                <ListItemText primary={t(literal)}></ListItemText>
                                            </ListItem>
                                        ))}
                                    </List>
                                </div>
                            );
                        })}
                    </RadioGroup>
                </FormControl>
                {!!subTypes.length && (
                    <>
                        <Typography className={classes.title}>{t('select.type.representation')}</Typography>
                        <div className={classes.typeTabs}>
                            {subTypes.map(({ key }, index) => (
                                <Chip
                                    icon={selectedSubType === key ? <SelectedChipIcon /> : <></>}
                                    key={`${key}_${index}`}
                                    className={`${classes.tabChip}${selectedSubType === key ? ' ' + classes.selectedTab : ''}`}
                                    label={t(key)}
                                    onClick={(e) => chooseSubType(e, key)}
                                />
                            ))}
                        </div>
                    </>
                )}
                <Grid container direction={'column'} className={classes.formWrapper}></Grid>
                {showLegalStatus && (
                    <Grid item xs sm md className={classes.gridItem}>
                        <InputLabel role="input" error={!!error || (legalStatus.isDirty && !legalStatus.value)} className={classes.fieldLabel}>
                            {t('legal.status')}
                        </InputLabel>
                        <Select
                            className={classes.textInput}
                            displayEmpty
                            error={!!error || (legalStatus.isDirty && !legalStatus.value)}
                            label={t('select.options')}
                            onChange={handleLegalStatusChange}
                            value={selectedSubType === 'association' ? 'business.company' : legalStatus.value || ''}
                            IconComponent={DropDownArrow}
                            disabled={selectedSubType === 'association' || selectedSubType === 'euipo.professional.practitioner'}
                            inputProps={{ 'aria-haspopup': 'listbox' }}>
                            <MenuItem value={''} disabled className={classes.disabledMenuItem}>
                                {t('select.options')}
                            </MenuItem>
                            {legalStatusOptions.map(({ key, literal }, idx) => (
                                <MenuItem key={`${key}_${idx}`} value={literal} role="option">
                                    {t(literal)}
                                </MenuItem>
                            ))}
                        </Select>
                        {legalStatus.isDirty && !legalStatus.value && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t('required.field')}
                            </Typography>
                        )}
                    </Grid>
                )}
                {showFirstName && (
                    <Grid item xs md sm className={classes.gridItem}>
                        <InputLabel
                            role="input"
                            error={!!error || (firstName.isDirty && !firstName.value)}
                            htmlFor={'firstName'}
                            className={classes.fieldLabel}>
                            {t('first.name')}*
                        </InputLabel>
                        <Input
                            id={'firstName'}
                            role="input"
                            className={classes.textInput}
                            placeholder={t('type.your.name')}
                            type={'text'}
                            name={'firstName'}
                            error={!!error || (firstName.isDirty && !firstName.value)}
                            fullWidth
                            autoFocus
                            required
                            onChange={handleFirstNameChange}
                            value={firstName.value}
                        />
                        {firstName.isDirty && !firstName.value && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t('required.field')}
                            </Typography>
                        )}
                    </Grid>
                )}
                {showSurName && (
                    <Grid item xs md sm className={classes.gridItem}>
                        <InputLabel
                            role="input"
                            error={!!error || (surName.isDirty && !surName.value)}
                            htmlFor={'surName'}
                            className={classes.fieldLabel}>
                            {t('surname')}*
                        </InputLabel>
                        <Input
                            id={'surName'}
                            role="input"
                            className={classes.textInput}
                            placeholder={t('type.your.surname')}
                            type={'text'}
                            name={'surName'}
                            error={!!error || (surName.isDirty && !surName.value)}
                            fullWidth
                            required
                            onChange={handleLastNameChange}
                            value={surName.value}
                        />
                        {surName.isDirty && !surName.value && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t('required.field')}
                            </Typography>
                        )}
                    </Grid>
                )}
                {showLegalName && (
                    <Grid item xs md sm className={classes.gridItem}>
                        <InputLabel
                            role="input"
                            error={!!error || (legalName.isDirty && !legalName.value)}
                            htmlFor={'legalName'}
                            className={classes.fieldLabel}>
                            {t('legal.name')}*
                        </InputLabel>
                        <Input
                            id={'legalName'}
                            role="input"
                            className={classes.textInput}
                            placeholder={t('type.your.legal.name')}
                            type={'text'}
                            name={'legalName'}
                            error={!!error || (legalName.isDirty && !legalName.value)}
                            fullWidth
                            required
                            onChange={handleLegalNameChange}
                            value={legalName.value}
                        />
                        {legalName.isDirty && !legalName.value && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t('required.field')}
                            </Typography>
                        )}
                    </Grid>
                )}
                {showOrganisationName && (
                    <Grid item xs md sm className={classes.gridItem}>
                        <InputLabel
                            role="input"
                            error={!!error || (organizationName.isDirty && !organizationName.value)}
                            htmlFor={'organizationName'}
                            className={classes.fieldLabel}>
                            {t('organisation.name')}*
                        </InputLabel>
                        <Input
                            id={'organizationName'}
                            role="input"
                            className={classes.textInput}
                            placeholder={t('type.organisation.name')}
                            type={'text'}
                            name={'organizationName'}
                            error={!!error || (organizationName.isDirty && !organizationName.value)}
                            fullWidth
                            autoFocus
                            required
                            onChange={handleOrganizationNameChange}
                            value={organizationName.value}
                        />
                        {organizationName.isDirty && !organizationName.value && (
                            <Typography variant={'body2'} color={'error'} className={classes.inputError}>
                                {t('required.field')}
                            </Typography>
                        )}
                    </Grid>
                )}
                <Grid container spacing={0} className={classes.buttonGroup}>
                    <Grid item>
                        <Button type={'button'} onClick={goBack}>
                            {t('back')}
                        </Button>
                    </Grid>
                    {!!userType && !!selectedSubType && (!showLegalStatus || !!legalStatus.value) && (
                        <Grid item>
                            <Button variant={'outlined'} className={classes.submitButton} type={'submit'}>
                                {t('next')}
                            </Button>
                        </Grid>
                    )}
                </Grid>
            </form>
        </div>
    );
};

export default StepTwo;
